import React, { useMemo } from 'react';
import { IUserProfile } from '../../../../types/User/IUserProfile';
import { IAction } from '../../../../types/Action/IAction';
import ActionButtons from './ActionButtons';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { Avatar } from '@progress/kendo-react-layout';
import ReactTimeAgo from 'react-timeago'
import { Setting } from '../../../../types/Setting/Setting';
import { UserApi } from '../../../../redux/api/UserApi';
import * as svg from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import AutoLinker from './Tools/AutoLinker';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useReduxDispatch, useReduxSelector } from '../../../../redux/hooks';
import { SetSelectedActions } from '../../../../slices/SelectedActionsSlice';
import { nanoid } from 'nanoid';

interface ActionNoteProps {
    action: IAction;
    userProfile: IUserProfile;
    workflowStepCompleted: (action: IAction, edit: boolean) => void;
    actionSelected: (action: IAction) => void;
    isSelected: boolean;
    showSelect: boolean;
}

const Action: React.FC<ActionNoteProps> = ({ showSelect, isSelected, actionSelected, action, userProfile, workflowStepCompleted }) => {
    const { data: notesColorSetting } = UserApi.useGetSettingQuery(Setting.END_USER_NOTES_COLOR);
    const getBackgroundColor = (action: IAction) => {
        if (action?.Pinned) return '#e1f8b9';

        return action?.Is_Client_Role && !action.Is_System_Action && notesColorSetting?.Bool_Value && notesColorSetting?.String_Value
    }


    //Action the action while the ID doesn't change
    const actionControl = useMemo(() => {
        if (action.Is_System_Action)
            return systemAction(action, userProfile);

        return (
            <div className={isSelected ? 'action selected' : 'action'} key={action.ID}>
                <div className="flex-grow">
                    <div className={isSelected ? 'header selected-nope' : 'header'} style={{ backgroundColor: getBackgroundColor(action) }}>
                        {showSelect && <div className="chk-col" >
                            {/* <Checkbox className='form-control' /> */}
                            <input type="checkbox" className='m-2 k-checkbox k-checkbox-md k-rounded-md' onChange={() => actionSelected(action)} checked={isSelected} />
                        </div>}
                        {action.Staff_Id > 0 && userProfile?.Features?.Profile_Action_Images && <Avatar className='me-1' rounded='full'><img src={action.Is_System_Action ? './images/Host_Images/System_Action/Gears.png' : action.Profile_Image_Url || './images/placeholder_sml.png'} alt="Profile" width="50" height="50" /></Avatar>}
                        <a className='d-flex flex-grow flex-column ms-2 text-decoration-none' href='#' title={'Select ' + userProfile.Phrases.Action} onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            actionSelected(action);
                        }}>
                            <h6 className='text-info action-author'>
                                {action.Is_System_Action ? 'System' : <>{action.Staff_Name} {action.Role_Name && !userProfile.Features.External_Interface && <small>({action.Role_Name})</small>}</>}{actionIcon(action)}
                                {action.Action_Type_Id === 14 && <SplitButton title={'Complete this step'} svgIcon={svg.checkboxIcon} items={[{ text: 'Complete and Edit Action' }]}
                                    onItemClick={() => workflowStepCompleted(action, true)}
                                    onButtonClick={() => workflowStepCompleted(action, false)} className="complete-workflow-step" text='Complete'></SplitButton>}
                                {action.Action_Type_Id === 15 && <SplitButton disabled title={'Complete this step'} svgIcon={svg.checkboxIcon} fillMode='outline' style={{ width: '150px' }} text='Complete'></SplitButton>}
                            </h6>
                            {action.ID > 0 && <small className="text-muted">{userProfile.Show_Friendly_Dates
                                ? <ReactTimeAgo date={new Date(action.Action_Dt + 'Z')} title={new Date(action.Action_Dt + 'Z').toLocaleDateString() + ' ' + new Date(action.Action_Dt + 'Z').toLocaleTimeString()} ></ReactTimeAgo>
                                : new Date(action.Action_Dt + 'Z').toLocaleDateString() + ' ' + new Date(action.Action_Dt + 'Z').toLocaleTimeString()}</small>}
                        </a>
                        <div className='d-flex'>
                            <div className='d-flex flex-row-reverse flex-wrap align-items-start' style={{ color: action.Action_Type_Id === 15 && 'gray' }}>
                                <div className='d-flex' >
                                    <ActionButtons {...action} />
                                </div>
                                {!userProfile.Features.External_Interface && action?.Action_Type_Id != 8 && !action.Is_System_Action && <div className='time me-2 mt-1'>
                                    <div><small className="text-muted">{userProfile?.Features?.Non_Bill_Time ? "Bill:" : "Time:"}</small> {action.Bill_Min}</div>
                                    {userProfile?.Features?.Non_Bill_Time && <div className='ms-3'><small className='text-muted'>Non-Bill:</small> {action.Non_Bill_Min}</div>}</div>}
                            </div>

                        </div>
                    </div>
                    <p className='note ms-3' style={{ color: action.Action_Type_Id === 15 && 'gray' }}><AutoLinker text={action.HTML_Notes ?? action.Action_Notes} /></p>
                </div>
            </div>)
    }
        , [action, isSelected, actionSelected]);

    return actionControl;
};

const systemAction = (action: IAction, userProfile: IUserProfile) => {
    return (
        <div className='system-action d-flex justify-content-center align-items-center'>
            <div className='vertical-line'></div>
            <div className='notes'>{actionIcon(action)} <AutoLinker text={action.HTML_Notes ?? action.Action_Notes} />
                {action.ID > 0 && <div className="action-date text-muted">{userProfile.Show_Friendly_Dates
                    ? <ReactTimeAgo date={new Date(action.Action_Dt + 'Z')} title={new Date(action.Action_Dt + 'Z').toLocaleDateString() + ' ' + new Date(action.Action_Dt + 'Z').toLocaleTimeString()} ></ReactTimeAgo>
                    : new Date(action.Action_Dt + 'Z').toLocaleDateString() + ' ' + new Date(action.Action_Dt + 'Z').toLocaleTimeString()}</div>}
            </div>
            <div className='vertical-line'></div>
        </div>
    )
}


const actionIcon = (action: IAction) => {

    var icon = null;
    if (action.Is_System_Action) {
        //System
        icon = <SvgIcon icon={svg.gearIcon} className='' />
    } else if (action.Action_Type_Id === 20) {
        //Timesheet
        icon = <SvgIcon icon={svg.clockIcon} className='mr-1' />
    } else if (action.Action_Type_Id === 7) {
        //Private
        icon = <SvgIcon icon={svg.lockIcon} className='mr-1' />
    } else if (action.Action_Type_Id === 30) {
        //Email to action
        icon = <SvgIcon icon={svg.paperPlaneIcon} className='mr-1' />
    } else if (action.Action_Type_Id === 17) {
        //Calendar
        icon = <SvgIcon icon={svg.calendarIcon} className='mr-1' />
    }

    if (icon)
        return <span title={action.Action_Type_Name} >{icon}</span>
}

export default Action;